"use strict"

import React from "react"
import propTypes from "prop-types"

import { COLORS_SCHEMECAT_10 } from "../graphing/resources/utils"

import {
  isLTPM,
  onGraphConfigMount,
  onGraphConfigUpdate,
  withAppSettingConfig,
} from "../../components/shared_components_registration"
import { withPlotSettingsConfig } from "./PlotSettingsConfigProvider"

export const GraphConfigContext = React.createContext({})

export function withGraphState(Component) {
  return function (props) {
    return (
      <GraphConfigContext.Consumer>
        {(graphState) => <Component {...props} {...graphState} />}
      </GraphConfigContext.Consumer>
    )
  }
}

class GraphConfigProvider extends React.Component {
  componentDidMount() {
    onGraphConfigMount(this)
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    onGraphConfigUpdate(this, prevProps)
    const { config = {}, timeExtractor, setTSTimeExtractor, setDayTimeExtractor } = this.props
    const useDefaultTimestamp = config?.settings?.DEFAULT_TIMESTAMP_ON_XAXIS
    if (isLTPM && useDefaultTimestamp !== prevProps?.config?.settings?.DEFAULT_TIMESTAMP_ON_XAXIS) {
      if (useDefaultTimestamp && timeExtractor.name !== "Timestamp" && setTSTimeExtractor) {
        setTSTimeExtractor()
      } else if (!useDefaultTimestamp & (timeExtractor.name !== "Day") && setDayTimeExtractor) {
        setDayTimeExtractor()
      }
    }
  }

  constructor(props) {
    super(props)

    this.state = {
      // LTPM specific state variables
      site: "",
      special_graphs: [],
      // --- LTPM specific state variables

      // LanzaLake specific state variables
      LTPM_PLOTS: [],
      plots: [],
      site_plots: [],
      special_plots: [],
      searchGraphs: [],
      selectedMutation: null,
      frequencyThreshold: 0.75,
      laboratory_sites: [],
      // --- LanzaLake specific state variables

      // Shared state variables
      live_graphs: [],
      graphs: [],
      default_graph_configs: [],
      initialConfigs: {},
      plots_curated_by: "",
      colors: COLORS_SCHEMECAT_10,
      performance_metrics_panel: [],
      report_metrics: [],
      queryTags: [],
      // To show/hide ActionLayer items
      show_site_actions: true,
      show_actions: true,
      show_critical_actions: true,
      show_annotations: true,
      // --- Shared state variables
    }

    this.updateActionLayerVisibility = this.updateActionLayerVisibility.bind(this)
    this.updateLiveGraphs = this.updateLiveGraphs.bind(this)
    this.updateGraphs = this.updateGraphs.bind(this)
    this.restoreDefaultGraphs = this.restoreDefaultGraphs.bind(this)
    this.updatePlots = this.updatePlots.bind(this)
    this.updateSitePlots = this.updateSitePlots.bind(this)
    this.updateSearchGraphs = this.updateSearchGraphs.bind(this)
    this.selectMutation = this.selectMutation.bind(this)
    this.setFrequencyThreshold = this.setFrequencyThreshold.bind(this)
  }

  updateActionLayerVisibility(metric, value) {
    this.setState({
      [metric]: value,
    })
  }

  parseGraphs = (graph_obj) => {
    const parseBoolean = (val) => {
      // Sometimes the boolean fields are passed as a string (e.g. "true" instead of true). Cleaning that up here.
      try {
        return JSON.parse(val)
      } catch (e) {
        return val
      }
    }

    const graphs = parseBoolean(graph_obj) || []
    const bool_variables = ["outlierFilter", "outlierFilter2", "hideOffline", "hideOnline", "gridlines"]
    const obs_variables = ["tags", "observations"]
    const bool_obs_variables = ["axis2", "disabled", "optional"]

    let final_graphs = []
    graphs.forEach((g) => {
      let graph = { ...g }
      bool_variables.forEach((metric) => {
        if (Object.keys(graph).includes(metric)) graph[metric] = parseBoolean(graph[metric])
      })
      obs_variables.forEach((metric) => {
        if (Object.keys(graph).includes(metric)) {
          let obs_arr = []
          graph[metric].forEach((obs) => {
            let o = { ...obs }
            bool_obs_variables.forEach((metric) => {
              if (Object.keys(o).includes(metric)) o[metric] = parseBoolean(o[metric])
            })
            obs_arr.push(o)
          })
          graph[metric] = obs_arr
        }
      })
      final_graphs.push(graph)
    })

    return final_graphs
  }

  render() {
    return (
      <GraphConfigContext.Provider
        value={{
          // LanzaLake Only
          laboratory_sites: this.state.laboratory_sites,
          plots: this.state.plots,
          site_plots: this.state.site_plots,
          searchGraphs: this.state.searchGraphs,
          selectedMutation: this.state.selectedMutation,
          selectMutation: this.selectMutation,
          frequencyThreshold: this.state.frequencyThreshold,
          setFrequencyThreshold: this.setFrequencyThreshold,
          updatePlots: this.updatePlots,
          updateSitePlots: this.updateSitePlots,
          updateSearchGraphs: this.updateSearchGraphs,

          // Shared
          colors: this.state.colors,
          queryTags: this.state.queryTags,
          live_graphs: this.state.live_graphs,
          default_graph_configs: this.state.default_graph_configs,
          live_run_focus: this.state.live_run_focus,
          special_graphs: isLTPM
            ? this.state.special_graphs
            : this.props.live
              ? [...this.state.LTPM_PLOTS]
              : [...this.state.special_plots],
          performance_metrics_panel: this.state.performance_metrics_panel,
          report_metrics: this.state.report_metrics,
          graphs: this.state.graphs,
          plots_curated_by: this.state.plots_curated_by,
          show_site_actions: this.state.show_site_actions,
          show_actions: this.state.show_actions,
          show_critical_actions: this.state.show_critical_actions,
          show_annotations: this.state.show_annotations,
          updateLiveGraphs: this.updateLiveGraphs,
          updateGraphs: this.updateGraphs,
          restoreDefaultGraphs: this.restoreDefaultGraphs,
          updateActionLayerVisibility: this.updateActionLayerVisibility,
        }}
      >
        {this.props.children}
      </GraphConfigContext.Provider>
    )
  }

  graphUpdate(update, graph_set) {
    if (typeof update === "function") {
      this.setState((state) => {
        const newGraphs = update(state[graph_set])
        if (newGraphs) {
          return {
            [graph_set]: newGraphs,
          }
        } else {
          return null
        }
      })
    } else {
      this.setState({
        [graph_set]: update,
      })
    }
  }

  updateGraphs(update) {
    this.graphUpdate(update, "graphs")
  }

  updateLiveGraphs(update) {
    this.graphUpdate(update, "live_graphs")
  }

  // LanzaLake Specific

  updatePlots(update) {
    this.graphUpdate(update, "plots")
  }

  updateSitePlots(update) {
    this.graphUpdate(update, "site_plots")
  }

  updateSearchGraphs(update) {
    this.graphUpdate(update, "searchGraphs")
  }

  restoreDefaultGraphs(type) {
    this.setState({
      [type]: this.state.initialConfigs[type],
    })
  }

  selectMutation(mut) {
    this.setState({
      selectedMutation: mut,
    })
  }

  setFrequencyThreshold(threshold) {
    this.setState({
      frequencyThreshold: threshold,
    })
  }

  // --- LanzaLake Specific
}

export default withAppSettingConfig(withPlotSettingsConfig(GraphConfigProvider))

GraphConfigProvider.propTypes = {
  children: propTypes.node.isRequired,
  live: propTypes.bool,
  site: propTypes.string,
}
